/**
 * AlertBoardPage (shared) — the operator's alert board, in any of its three presentations,
 * behind a toggle in the toolbar.
 *
 * The honeycomb (every camera, clustered site → group), the GIS board (cameras pinned on a
 * floor plan) and the map (cameras at their real-world coordinates) were separate pages
 * showing the SAME live data through the same `useAlertBoard` engine, the same colour
 * language and the same docked alert panel. A sidebar entry per board meant an operator who
 * wanted a different read of an alert had to leave the page and lose their position; one page
 * with a toggle makes it one click in place.
 *
 * WHAT EACH ONE ANSWERS — which is why there are three and not fewer:
 *   · Honeycomb — "which camera", every camera at once, nothing spatial
 *   · GIS       — "which door", pinned on a plan of the premises
 *   · Map       — "which building, which street", pinned on OpenStreetMap
 *
 * The board formerly LABELLED "Map View" is the floor-plan one; it is now GIS View, and the
 * name Map View belongs to the geographic board, where it means what an operator expects.
 *
 * Only the LAYOUT swaps. Which board is on screen is a presentation choice, not a different
 * surface, so the toggle is a segmented control rather than tabs, and it lives in the app's
 * TITLE BAR beside the page name — it belongs to the page, not to any one board, and the
 * boards' own cards are left to their canvas and its floating controls. The title also carries
 * the "N open · N live" pill, whose numbers each board reports up through `onStats`, and a
 * "Set default" button that pins the view this board opens on.
 *
 * The boards are mounted exclusively, never all-but-one-hidden: each holds live WebRTC
 * subscriptions and its own realtime watch, and paying for the idle ones so they stay warm
 * would multiply every stream on the page.
 *
 * Node-agnostic like the boards it wraps: `alertsApi` / `mapApi` / `geoApi` and the video
 * player are injected, so local passes SFU wiring and cloud passes its per-node tunnel wiring.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CheckIcon, GlobeAltIcon, MapPinIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import AlertHexagonPage from './AlertHexagonPage';
import CameraMapPage from './CameraMapPage';
import CameraGeoMapPage from './CameraGeoMapPage';
import SegmentedToggle, { type SegmentedOption } from '../components/common/SegmentedToggle';
import { PageTitle } from '../components/layout/TitleBar';
import type { BoardStats } from '../components/hexagon/BoardChrome';
import type { HexPlayerComponent, ViewAlertRequest } from '../components/hexagon/types';
import type { GeoMapApi, HexAlertsApi, MapLayoutApi } from '../api/contracts';

/** `gis` is the floor-plan board (it was called `map` before the geographic board existed);
 *  `map` is now the geographic one. See the storage-key note below — the rename is not free. */
export type BoardView = 'hex' | 'gis' | 'map';

/**
 * VERSIONED on purpose. Under the previous key a stored `'map'` meant the FLOOR PLAN, and
 * reusing it would open the geographic board for every operator who had last used the plan —
 * silently, and on the one board they had not asked for. A new key simply starts them on the
 * honeycomb once.
 */
const STORAGE_KEY = 'trinetra.alertboard.view.v2';
/** The operator's explicit "open this board on…" choice (Set default). Separate from the
 *  remembered last view, and it wins over it on the way in. */
const DEFAULT_KEY = 'trinetra.alertboard.default.v1';

const readStored = (key: string | null): BoardView | null => {
  if (!key) return null;
  try {
    return localStorage.getItem(key) as BoardView | null;
  } catch {
    return null; // private mode — nothing was ever stored
  }
};

// The status pill's red tint. Inline: the theme's colours are bare var()s, so Tailwind's
// opacity modifiers (bg-destructive/10) compile to nothing.
const OPEN_PILL_STYLE: CSSProperties = {
  backgroundColor: 'color-mix(in srgb, var(--destructive) 12%, transparent)',
  color: 'var(--destructive)',
};

// Symbol + name. The icons are the sidebar's own — the hexagon board and Map View carried
// these before the merge, so an operator recognises them from the menu they used to be in.
// Short names: the title bar already says "Alert Board", so "View" on each segment was
// padding. The tooltip says what each view IS, which neither the name nor the symbol manages.
const VIEW_OPTIONS: Record<BoardView, SegmentedOption<BoardView>> = {
  hex: {
    key: 'hex',
    label: 'Honeycomb',
    icon: RectangleGroupIcon,
    title: 'Every camera, clustered by site',
  },
  gis: {
    key: 'gis',
    label: 'GIS',
    icon: MapPinIcon,
    title: 'Cameras pinned on a floor plan',
  },
  map: {
    key: 'map',
    label: 'Map',
    icon: GlobeAltIcon,
    title: 'Cameras at their real-world locations',
  },
};

export interface AlertBoardPageProps {
  /** Realtime alerts + camera roster. Backs ALL THREE boards. */
  alertsApi: HexAlertsApi;
  /** Floor-plan layouts and pin placements. Only the GIS board reads it. */
  mapApi: MapLayoutApi;
  /** Camera coordinates + the saved default view. Only the geographic board reads it.
   *  Optional so a caller that pins the board to another view needn't wire it. */
  geoApi?: GeoMapApi;
  PlayerComponent: HexPlayerComponent;
  /** Caller holds `map.edit` — shows the layout, pin and default-view editing affordances on
   *  both spatial boards. */
  canEdit?: boolean;
  /** Cloud: require a selected node; the boards show an empty state when nodeKey is falsy. */
  requireNode?: boolean;
  /** Cloud: changes when the selected node changes → both boards reload. */
  nodeKey?: string;
  /** Extra classes on the board in page mode. No padding by default — the layout's <main>
   *  already gives the gutter; full screen adds its own so panels stay off the bezel. */
  className?: string;
  onViewAlert?: (alert: ViewAlertRequest) => void;
  /** Jump to a camera's recorded footage from the live preview. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /**
   * This board covers ONE site — the honeycomb draws every group on a single shared
   * background instead of a panel per site. Local passes it (the node IS the site); cloud
   * leaves it off, where each node genuinely is a different place.
   */
  singleSite?: boolean;

  /**
   * Which views this user may see, in toolbar order. Defaults to all three.
   *
   * The boards carry different RBAC keys (`alerts.view` for the honeycomb, `map.view` for both
   * spatial boards), and merging the pages must not merge the permissions — a viewer entitled
   * to only one of them gets that one, with no toggle at all rather than a button that bounces
   * them to the dashboard.
   *
   * The two spatial boards deliberately SHARE `map.view`: they are one entitlement — "see
   * where my cameras are" — drawn on two backdrops, and splitting the key would withhold the
   * new board from every role that already holds the old one.
   */
  allowedViews?: BoardView[];

  /**
   * The current view. Controlled, because each app keeps it in the URL — that's what makes
   * the merged board deep-linkable, bookmarkable and able to light up the right sidebar item.
   */
  view: BoardView;
  /** Called with the view the operator picked. The app is expected to route to it. */
  onViewChange: (view: BoardView) => void;

  /**
   * Remember the operator's last view across sessions: on mount, a remembered view is
   * re-selected through `onViewChange`.
   *
   * Both sidebars point at the honeycomb's URL, so without this an operator who works from
   * the floor plan would have to toggle to it on every visit. Leave off where the caller pins
   * the view (Multi-Site) — there is nothing to remember.
   */
  rememberView?: boolean;
  /** Scopes what `rememberView` and "Set default" store. Cloud passes the node id, so each site
   *  keeps its own preference; local has one site and needs no suffix. */
  persistKey?: string;
}

export default function AlertBoardPage({
  alertsApi,
  mapApi,
  geoApi,
  PlayerComponent,
  canEdit = false,
  requireNode = false,
  nodeKey,
  className,
  onViewAlert,
  onOpenRecordings,
  allowedViews = ['hex', 'gis', 'map'],
  singleSite = false,
  view: requested,
  onViewChange,
  rememberView = false,
  persistKey,
}: AlertBoardPageProps) {
  // A geographic board with no api to read is not a board — drop it rather than mount a
  // page that can only render an error.
  const views = (allowedViews.length ? allowedViews : (['hex'] as BoardView[])).filter(
    (v) => v !== 'map' || !!geoApi,
  );
  const storageKey = rememberView ? `${STORAGE_KEY}${persistKey ? `.${persistKey}` : ''}` : null;

  // A view the caller no longer allows (permissions changed, or a deep link into the other
  // board) falls back rather than rendering a board this user must not see. The app wrappers
  // apply the same fallback to the URL, so the address bar doesn't end up naming a board that
  // isn't on screen.
  const view = views.includes(requested) ? requested : views[0];

  const setView = useCallback(
    (next: BoardView) => {
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, next);
        } catch {
          /* private mode — the choice just won't survive a reload */
        }
      }
      onViewChange(next);
    },
    [onViewChange, storageKey],
  );

  // ── Default view ("Set default") ─────────────────────────────────────────
  // Only where there is a choice to make. Scoped like the remembered view, but kept even where
  // remembering is off — it is something the operator said, not something we noticed.
  const defaultKey = views.length >= 2 ? `${DEFAULT_KEY}${persistKey ? `.${persistKey}` : ''}` : null;
  const [defaultView, setDefaultView] = useState<BoardView | null>(() => readStored(defaultKey));
  useEffect(() => setDefaultView(readStored(defaultKey)), [defaultKey]);

  const saveDefault = useCallback(() => {
    if (!defaultKey) return;
    try {
      localStorage.setItem(defaultKey, view);
    } catch {
      /* private mode — it holds for this visit only */
    }
    setDefaultView(view);
  }, [defaultKey, view]);

  // Restore once per key pair — so on the way in, and again when a key changes under us (cloud
  // scopes both per node, so switching site restores THAT site's choice). An explicit default
  // wins; otherwise the remembered last view. Not on every render: after the restore the
  // operator's clicks are the source of truth, and re-running would keep dragging them back.
  const restoreKey = storageKey || defaultKey ? `${defaultKey}|${storageKey}` : null;
  const restoredForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!restoreKey || restoredForRef.current === restoreKey) return;
    restoredForRef.current = restoreKey;
    const pinned = readStored(defaultKey);
    const remembered = readStored(storageKey);
    const pick = pinned && views.includes(pinned) ? pinned : remembered && views.includes(remembered) ? remembered : null;
    if (pick && pick !== view) onViewChange(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreKey]);

  // The title bar's "N open · N live", reported by whichever board is mounted.
  const [stats, setStats] = useState<BoardStats>({ open: 0, live: 0 });
  const onStats = useCallback(
    (next: BoardStats) => setStats((cur) => (cur.open === next.open && cur.live === next.live ? cur : next)),
    [],
  );

  // Flipping views mid-edit would unmount the board being edited and drop the unsaved pins,
  // so the toggle locks until the operator saves or cancels. Both spatial boards report
  // through the same flag — only one of them is ever mounted.
  const [mapEditing, setMapEditing] = useState(false);
  const onEditingChange = useCallback((editing: boolean) => setMapEditing(editing), []);
  // The board is what owns this state; leaving it can't leave the lock behind.
  useEffect(() => {
    if (view === 'hex') setMapEditing(false);
  }, [view]);

  // ── Full screen ───────────────────────────────────────────────────────────
  // Two layers, because either alone falls short on a control-room screen:
  //   · the board is pinned over the app (fixed, above the header and sidebar), which
  //     works everywhere, including where the browser refuses the Fullscreen API;
  //   · and the browser goes fullscreen on the DOCUMENT, hiding its own chrome.
  // Fullscreening the document rather than the board element is deliberate: the alert
  // detail modal and the tooltips are portalled to <body>, and a fullscreened board
  // element would hide them — clicking an alert card would appear to do nothing.
  // z-[45]: above the sticky header (z-40), below modals (z-50), which must still open on top.
  const [fullscreen, setFullscreen] = useState(false);
  const browserFsRef = useRef(false);

  const exitFullscreen = useCallback(() => {
    setFullscreen(false);
    if (browserFsRef.current && document.fullscreenElement) document.exitFullscreen().catch(() => {});
    browserFsRef.current = false;
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (fullscreen) {
      exitFullscreen();
      return;
    }
    setFullscreen(true);
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      document.documentElement
        .requestFullscreen()
        .then(() => {
          browserFsRef.current = true;
        })
        .catch(() => {
          /* refused (iframe, policy) — the in-page layer alone still gives the board the screen */
        });
    }
  }, [fullscreen, exitFullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    // The browser's own exit (Esc, F11) must drop the in-page layer too, or the operator is
    // left with a board pinned over the app and no browser fullscreen. A video tile leaving
    // ITS fullscreen hands back to the document, which keeps fullscreenElement set — so only
    // a real exit gets here.
    const onChange = () => {
      if (browserFsRef.current && !document.fullscreenElement) {
        browserFsRef.current = false;
        setFullscreen(false);
      }
    };
    // Where the browser refused, Esc is ours to handle. Not while a dialog is open: Esc is
    // that dialog's close, and one press shouldn't do both.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.fullscreenElement) return;
      if (document.querySelector('[role="dialog"]')) return;
      setFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      window.removeEventListener('keydown', onKey);
    };
  }, [fullscreen]);

  // The way out of full screen is the board's own full-screen button, top-right of its card —
  // inside the frame, so it stays on screen (it used to be a separate proximity-revealed exit
  // button, from when full screen hid the toolbar the button lived in). Esc still works.

  // Leaving the page while fullscreen must not strand the browser in it.
  useEffect(
    () => () => {
      if (browserFsRef.current && document.fullscreenElement) document.exitFullscreen().catch(() => {});
    },
    [],
  );

  // The view toggle. Rendered in the title bar normally; the full-screen frame covers the title
  // bar, so there the same control floats at the top centre of the board card instead.
  const viewToggle = useMemo(
    () =>
      views.length >= 2 ? (
        <SegmentedToggle<BoardView>
          aria-label="Board view"
          options={views.map((v) => VIEW_OPTIONS[v])}
          value={view}
          onChange={setView}
          disabled={mapEditing}
          disabledTitle="Save or cancel the layout first"
          // Floating over the canvas it needs the cards' own lift.
          className={fullscreen ? 'shadow-card' : ''}
        />
      ) : null,
    // `views` is a fresh array each render; its CONTENT is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [views.join(','), view, setView, mapEditing, fullscreen],
  );
  const centerSlot = fullscreen ? viewToggle : null;

  const isDefault = defaultView === view;

  // Every board renders inside this one element, so going fullscreen only changes its class —
  // nothing remounts and no live stream renegotiates. Only fullscreen pads the board; it is
  // what keeps the panels off the bezel.
  const frameClass = fullscreen ? 'fixed inset-0 z-[45] bg-background' : 'h-full';
  const boardClass = fullscreen ? 'p-3 sm:p-4' : (className ?? '');

  let board: ReactNode;
  if (view === 'map' && geoApi) {
    board = (
      <CameraGeoMapPage
        api={geoApi}
        alertsApi={alertsApi}
        PlayerComponent={PlayerComponent}
        canEdit={canEdit}
        requireNode={requireNode}
        nodeKey={nodeKey}
        className={boardClass}
        onViewAlert={onViewAlert}
        onOpenRecordings={onOpenRecordings}
        centerSlot={centerSlot}
        fullscreen={fullscreen}
        onToggleFullscreen={toggleFullscreen}
        onEditingChange={onEditingChange}
        onStats={onStats}
      />
    );
  } else if (view === 'gis') {
    board = (
      <CameraMapPage
        api={mapApi}
        alertsApi={alertsApi}
        PlayerComponent={PlayerComponent}
        canEdit={canEdit}
        requireNode={requireNode}
        nodeKey={nodeKey}
        className={boardClass}
        onViewAlert={onViewAlert}
        onOpenRecordings={onOpenRecordings}
        centerSlot={centerSlot}
        fullscreen={fullscreen}
        onToggleFullscreen={toggleFullscreen}
        onEditingChange={onEditingChange}
        onStats={onStats}
      />
    );
  } else {
    board = (
      <AlertHexagonPage
        api={alertsApi}
        PlayerComponent={PlayerComponent}
        requireNode={requireNode}
        nodeKey={nodeKey}
        className={boardClass}
        onViewAlert={onViewAlert}
        onOpenRecordings={onOpenRecordings}
        centerSlot={centerSlot}
        fullscreen={fullscreen}
        onToggleFullscreen={toggleFullscreen}
        singleSite={singleSite}
        onStats={onStats}
      />
    );
  }

  return (
    <>
      <PageTitle
        title="Alert Board"
        subtitle={
          stats.open > 0 ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-semibold tabular-nums"
              style={OPEN_PILL_STYLE}
            >
              <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: 'var(--destructive)' }} />
              {stats.open} open · {stats.live} live
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[12px] font-medium text-muted-foreground">
              No open alerts
            </span>
          )
        }
      >
        {viewToggle}
        {/* Pins the view this board OPENS on (per site on cloud). Once the current view is
            the default there is nothing to set, so it says so instead of offering a no-op. */}
        {defaultKey && (
          <button
            type="button"
            onClick={saveDefault}
            disabled={isDefault}
            title={isDefault ? 'This board opens on this view' : 'Open this board on this view from now on'}
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-default disabled:hover:bg-card disabled:hover:text-muted-foreground"
          >
            <CheckIcon className={`h-3.5 w-3.5 ${isDefault ? 'text-brand' : ''}`} />
            <span className="hidden sm:inline">{isDefault ? 'Default' : 'Set default'}</span>
          </button>
        )}
      </PageTitle>
      <div className={frameClass}>{board}</div>
    </>
  );
}
